import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { 
    Send, 
    CheckCircle2, 
    User, 
    Mail, 
    Phone, 
    MessageSquare,
    Building2,
    Loader2,
    AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactForm = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (value) => {
        setFormData(prev => ({ ...prev, subject: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        
        try {
            const response = await fetch(`${BACKEND_URL}/api/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to send message');
            }
            
            setIsSubmitted(true);
        } catch (err) {
            console.error('Error sending contact form:', err);
            setError(err.message || 'Failed to send message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
            setIsSubmitted(false);
            setError(null);
        }, 300);
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
                {!isSubmitted ? (
                    <>
                        <div className="bg-gradient-to-r from-primary to-primary-dark p-6 text-white">
                            <DialogHeader>
                                <DialogTitle className="text-2xl font-display font-bold text-white flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                                        <MessageSquare className="w-5 h-5" />
                                    </div>
                                    {t('contact.title')}
                                </DialogTitle>
                                <DialogDescription className="text-white/80 mt-2">
                                    {t('contact.description')}
                                </DialogDescription>
                            </DialogHeader>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-sm font-medium flex items-center gap-2">
                                    <User className="w-4 h-4 text-muted-foreground" />
                                    {t('contact.fullName')}
                                </Label>
                                <Input
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder={t('contact.namePlaceholder')}
                                    required
                                    className="h-11 rounded-xl"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2">
                                        <Mail className="w-4 h-4 text-muted-foreground" />
                                        {t('contact.email')}
                                    </Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder={t('contact.emailPlaceholder')}
                                        required
                                        className="h-11 rounded-xl"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone" className="text-sm font-medium flex items-center gap-2">
                                        <Phone className="w-4 h-4 text-muted-foreground" />
                                        {t('contact.phone')}
                                    </Label>
                                    <Input
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        placeholder={t('contact.phonePlaceholder')}
                                        className="h-11 rounded-xl"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="subject" className="text-sm font-medium flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-muted-foreground" />
                                    {t('contact.subject')}
                                </Label>
                                <Select onValueChange={handleSelectChange} value={formData.subject}>
                                    <SelectTrigger className="h-11 rounded-xl">
                                        <SelectValue placeholder={t('contact.subjectPlaceholder')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="loan">{t('contact.subjects.loan')}</SelectItem>
                                        <SelectItem value="mortgage">{t('contact.subjects.mortgage')}</SelectItem>
                                        <SelectItem value="real-estate">{t('contact.subjects.realEstate')}</SelectItem>
                                        <SelectItem value="business">{t('contact.subjects.business')}</SelectItem>
                                        <SelectItem value="other">{t('contact.subjects.other')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="message" className="text-sm font-medium flex items-center gap-2">
                                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                                    {t('contact.message')}
                                </Label>
                                <Textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder={t('contact.messagePlaceholder')}
                                    required
                                    rows={4}
                                    className="rounded-xl resize-none"
                                />
                            </div>

                            {error && (
                                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <Button 
                                type="submit" 
                                disabled={isSubmitting}
                                className="w-full h-12 btn-gold rounded-xl text-base font-semibold"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        {t('contact.sending')}
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-5 h-5 mr-2" />
                                        {t('contact.send')}
                                    </>
                                )}
                            </Button>
                        </form>
                    </>
                ) : (
                    <div className="p-8 text-center">
                        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 animate-scale-in">
                            <CheckCircle2 className="w-10 h-10 text-primary" />
                        </div>
                        <h3 className="font-display text-2xl font-bold text-foreground mb-3">
                            {t('contact.successTitle')}
                        </h3>
                        <p className="text-muted-foreground mb-6">
                            {t('contact.successMessage')}
                        </p>
                        <Button 
                            onClick={handleClose}
                            className="btn-premium text-white rounded-xl px-8"
                        >
                            {t('contact.close')}
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
